import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from '../components';
import { FiUser, FiCalendar, FiClock, FiArrowLeft, FiPlus, FiX, FiEdit, FiTrash2, FiUsers, FiEye } from 'react-icons/fi';
import { useStateContext } from '../contexts/ContextProvider';
import axios from 'axios';

const Batches = () => {
  const { contentId } = useParams();
  const navigate = useNavigate();
  const { currentColor } = useStateContext();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [liveClass, setLiveClass] = useState(null);
  const [batches, setBatches] = useState([]);
  const [batchData, setBatchData] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [teacherUsers, setTeacherUsers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  // Initial fetch
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch content (live class) details
        const contentResponse = await axios.get(`https://apissc.sainikschoolcadet.com/api/Contents/${contentId}`);
        setLiveClass(contentResponse.data);
        
        // Fetch batches for this content using the new endpoint
        const batchesResponse = await axios.get(`https://apissc.sainikschoolcadet.com/api/Batches/content/${contentId}`);
        setBatches(batchesResponse.data || []);
        
        // Fetch all teacher users
        const usersResponse = await axios.get('https://apissc.sainikschoolcadet.com/api/Users/getByRole/teacher');
        // Filter only teachers
        const teachers = usersResponse.data.filter(user => user.role === 'teacher');
        setTeacherUsers(teachers);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [contentId]);

  // Fetch additional data for each batch (student count and teachers)
  useEffect(() => {
    const fetchBatchDetails = async () => {
      if (batches.length === 0) return;

      const batchDetailsData = {};
      
      for (const batch of batches) {
        try {
          // Fetch student count
          const studentCountResponse = await axios.get(
            `https://apissc.sainikschoolcadet.com/api/BatchStudents/batch/${batch.batch_id}/count`
          );
          
          // Fetch teachers assigned to batch
          const teachersResponse = await axios.get(
            `https://apissc.sainikschoolcadet.com/api/BatchTeachers/batch/${batch.batch_id}/teachers`
          );
          
          // Create an array to store teacher data
          const teacherData = [];
          
          // Fetch details for each teacher
          for (const teacherId of teachersResponse.data.teacher_ids || []) {
            try {
              const teacherResponse = await axios.get(
                `https://apissc.sainikschoolcadet.com/api/Users/getbyid/${teacherId}`
              );
              
              if (teacherResponse.data) {
                teacherData.push({
                  id: teacherId,
                  name: teacherResponse.data.f_name,
                  email: teacherResponse.data.email
                });
              }
            } catch (teacherErr) {
              console.error(`Error fetching teacher ${teacherId}:`, teacherErr);
            }
          }
          
          batchDetailsData[batch.batch_id] = {
            studentCount: studentCountResponse.data.student_count,
            teachers: teacherData
          };
        } catch (err) {
          console.error(`Error fetching details for batch ${batch.batch_id}:`, err);
          batchDetailsData[batch.batch_id] = {
            studentCount: 0,
            teachers: []
          };
        }
      }
      
      setBatchData(batchDetailsData);
    };
    
    fetchBatchDetails();
  }, [batches]);
  const handleGoBack = () => { 
      navigate(`/live-classes`);
  };
  // Batch Form Component
  const BatchForm = () => {
    const [formState, setFormState] = useState(
      editingBatch ? {
        batch_name: editingBatch.batch_name || '',
        status: editingBatch.status || 'active'
      } : {
        batch_name: '',
        status: 'active'
      }
    );

    const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormState(prev => ({
        ...prev,
        [name]: value
      }));
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      setSubmitting(true);
      setFormError(null);
      
      try {
        // Create payload
        const batchData = {
          ...formState,
          content_id: parseInt(contentId, 10)
        };
        
        let updatedBatch;
        
        if (editingBatch) {
          // Update existing batch
          const response = await axios.put(
            `https://apissc.sainikschoolcadet.com/api/Batches/${editingBatch.batch_id}`,
            batchData
          );
          updatedBatch = response.data;
          
          // Update batches list
          setBatches(prevBatches => 
            prevBatches.map(batch => 
              batch.batch_id === editingBatch.batch_id ? updatedBatch : batch
            )
          );
        } else {
          // Create new batch
          const response = await axios.post(
            'https://apissc.sainikschoolcadet.com/api/Batches',
            batchData
          );
          updatedBatch = response.data;
          
          // Add to batches list
          setBatches(prevBatches => [...prevBatches, updatedBatch]);
        }
        
        // Close the form
        setShowForm(false);
        setEditingBatch(null);
      } catch (err) {
        console.error('Error saving batch:', err);
        setFormError(err.response?.data?.message || 'Failed to save batch. Please try again.');
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
        <div className="bg-white rounded-xl p-6 w-full max-w-xl" onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">
              {editingBatch ? 'Edit Batch' : 'Create New Batch'}
            </h2>
            <button 
              type="button" 
              onClick={() => {
                setShowForm(false);
                setEditingBatch(null);
              }} 
              className="p-2 rounded-full hover:bg-gray-100"
            >
              <FiX />
            </button>
          </div>
          
          {formError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-600 rounded-md">
              {formError}
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-6">
              {/* Batch Name */}
              <div>
                <label htmlFor="batch_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Batch Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="batch_name"
                  name="batch_name"
                  value={formState.batch_name}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Morning Batch - Class 10"
                />
              </div>
              
              {/* Status */}
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                  Status <span className="text-red-500">*</span>
                </label>
                <select
                  id="status"
                  name="status"
                  value={formState.status}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              
              {/* Note about teachers */}
              <div className="p-4 bg-blue-50 text-blue-700 rounded-md">
                <p className="text-sm">
                  <strong>Note:</strong> Teachers can be assigned to this batch after creation from the batch management page.
                </p>
              </div>
              
              {/* Submit Buttons */}
              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingBatch(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm mr-3"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white rounded-md shadow-sm"
                  style={{ backgroundColor: currentColor }}
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : (editingBatch ? 'Update Batch' : 'Create Batch')}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Handle batch deletion
  const handleDeleteBatch = async (batchId) => {
    if (!window.confirm('Are you sure you want to delete this batch? This action cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`https://apissc.sainikschoolcadet.com/api/Batches/${batchId}`);
      // Remove batch from state
      setBatches(prevBatches => prevBatches.filter(batch => batch.batch_id !== batchId));
    } catch (err) {
      console.error('Error deleting batch:', err);
      alert('Failed to delete batch. Please try again.');
    }
  };

  const handleViewBatch = (batchId) => {
    navigate(`/BatchView/${batchId}`);
  };
  // Get formatted teacher names list for a batch
  const getTeachersList = (batchId) => {
    const teachers = batchData[batchId]?.teachers || [];
    if (teachers.length === 0) return 'No teachers assigned';
    
    return teachers.map(teacher => teacher.name).join(', ');
  };

  // If loading
  if (loading) {
    return (
      <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
        <Header category="Content" title="Batch Management" />
        <div className="flex justify-center items-center h-64">
          <div className="animate-pulse flex flex-col items-center">
            <div className="w-12 h-12 rounded-full bg-gray-200 mb-3"></div>
            <p className="text-xl text-gray-600">Loading batch details...</p>
          </div>
        </div>
      </div>
    );
  }

  // If error
  if (error) {
    return (
      <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
        <Header category="Content" title="Batch Management" />
        <div className="mt-4 p-4 bg-red-50 border border-red-200 text-red-600 rounded-md">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
      {/* Header */}
      <div className="mb-8">
       {/* Back button */}
<button 
          onClick={handleGoBack}
          className="mb-4 flex items-center text-gray-600 hover:text-gray-900"
        >
          <FiArrowLeft className="mr-2" /> Back to Content
        </button>
        
        <Header 
          category="Live Class" 
          title={liveClass?.title || 'Class Details'} 
        />
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mt-2">
          <p className="text-gray-600">{liveClass?.description || 'No description available'}</p>
          
          {/* Updated Price Display */}
<div className="mt-4 sm:mt-0 flex items-center">
  {liveClass?.max_price > liveClass?.sell_price ? (
    <>
      <div className="px-3 py-1 rounded-full bg-gray-100 text-gray-800 line-through text-sm">
        ₹{liveClass?.max_price}
      </div>
      <div className="ml-2 px-3 py-1 rounded-full bg-blue-100 text-blue-800 font-semibold text-sm">
        ₹{liveClass?.sell_price}
      </div>
      <div className="ml-2 px-3 py-1 rounded-full bg-green-100 text-green-800 text-sm">
        Save: ₹{liveClass?.max_price - liveClass?.sell_price}
      </div>
    </>
  ) : (
    <div className="px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm">
      Price: ₹{liveClass?.sell_price || 0}
    </div>
  )}
</div>
        </div>
      </div>
      
      {/* Batches Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">Available Batches</h3>
          <p className="text-sm text-gray-500">Manage batches for this live class</p>
        </div>
        <button 
          className="px-4 py-2 rounded-md text-white text-sm flex items-center gap-2"
          style={{ backgroundColor: currentColor }}
          onClick={() => {
            setEditingBatch(null);
            setShowForm(true);
          }}
        >
          <FiPlus /> Add New Batch
        </button>
      </div>
      
      {/* Batch List */}
      {batches.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <div className="flex justify-center mb-4">
            <div className="p-4 rounded-full bg-gray-200 text-gray-400">
              <FiCalendar size={24} />
            </div>
          </div>
          <p className="text-gray-600 mb-4">No batches available for this live class yet.</p>
          <button 
            className="px-4 py-2 rounded-md text-white text-sm inline-flex items-center gap-2"
            style={{ backgroundColor: currentColor }}
            onClick={() => {
              setEditingBatch(null);
              setShowForm(true);
            }}
          >
            <FiPlus /> Create First Batch
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {batches.map((batch) => (
            <div key={batch.batch_id} className="border rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow">
              <div className="p-6">
                <div className="flex flex-col md:flex-row">
                  <div className="flex-grow">
                    <div className="flex flex-col md:flex-row md:justify-between md:items-start">
                      <div>
                        <div className="flex items-center mb-2">
                          <h3 className="text-xl font-semibold">{batch.batch_name}</h3>
                          <span className={`ml-3 px-2 py-1 text-xs rounded-full ${
                            batch.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {batch.status.charAt(0).toUpperCase() + batch.status.slice(1)}
                          </span>
                        </div>
                        <p className="text-gray-600 mb-2 flex items-center">
                          <FiUser className="inline mr-2" /> 
                          Teachers: {getTeachersList(batch.batch_id)}
                        </p>
                        <p className="text-gray-600 mb-2 flex items-center">
                          <FiUsers className="inline mr-2" /> 
                          Students: {batchData[batch.batch_id]?.studentCount || 0}
                        </p>
                      </div>
                      <div className="mt-4 md:mt-0 flex space-x-2">
                        <button 
                          onClick={() => {
                            setEditingBatch(batch);
                            setShowForm(true);
                          }}
                          className="p-2 rounded-full hover:bg-gray-100 text-gray-600"
                          title="Edit Batch"
                        >
                          <FiEdit />
                        </button>
                        <button 
                          onClick={() => handleDeleteBatch(batch.batch_id)}
                          className="p-2 rounded-full hover:bg-red-100 text-red-600"
                          title="Delete Batch"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </div>
                    
                    <div className="mt-6 flex justify-between items-center">
                      <div className="flex items-center">
                        <FiClock className="mr-2 text-gray-500" />
                        <span className="text-sm text-gray-500">
                          Created: {new Date(batch.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      
                      <div className="flex gap-2">
                      <button 
      onClick={() => handleViewBatch(batch.batch_id)}
      className="px-4 py-2 rounded-md text-white text-sm"
      style={{ backgroundColor: currentColor }}
    >
      View
    </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Batch Form Modal */}
      {showForm && <BatchForm />}
    </div>
  );
};

export default Batches;